import {
  PAGE_SETTINGS_REGISTRY,
  COMMON_LIST_FIELDS,
  PREFIX_DOMAIN,
  type PageSettingsDef,
  type PageSettingLabel,
} from '../../data/pageSettingsRegistry'
import { pathMatches } from '../guide/guideResolver'

function scoreMatch(pattern: string, pathname: string): number {
  if (!pathMatches(pattern, pathname)) return -1
  if (pattern.endsWith('*')) return pattern.length - 1
  if (pattern.includes(':')) return pattern.length + 500
  if (pathname === pattern) return pattern.length + 2000
  return pattern.length
}

function humanizePath(pathname: string): PageSettingLabel {
  const last = pathname.split('/').filter(Boolean).pop() ?? 'page'
  const nice = last.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  return { ar: nice, en: nice }
}

function toPageId(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean)
  if (parts.length === 0) return 'Home'
  return parts
    .map((p) => p.replace(/[^a-zA-Z0-9]+/g, ' '))
    .join(' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('')
}

function findInRegistry(pathname: string): PageSettingsDef | null {
  let best: PageSettingsDef | null = null
  let bestScore = -1

  for (const def of PAGE_SETTINGS_REGISTRY) {
    const patterns = Array.isArray(def.match) ? def.match : [def.match]
    for (const p of patterns) {
      const score = scoreMatch(p, pathname)
      if (score > bestScore) {
        best = def
        bestScore = score
      }
    }
  }
  return best
}

function resolvePrefix(pathname: string) {
  let best: (typeof PREFIX_DOMAIN)[number] | null = null
  let bestLen = -1
  for (const row of PREFIX_DOMAIN) {
    if (pathname === row.prefix || pathname.startsWith(row.prefix)) {
      if (row.prefix.length > bestLen) {
        best = row
        bestLen = row.prefix.length
      }
    }
  }
  return best
}

/**
 * Resolve page-settings definition for the current route.
 * Known pages use the registry; others get a sensible fallback with common list fields.
 */
export function resolvePageSettings(pathname: string): PageSettingsDef | null {
  if (!pathname || pathname === '/login' || pathname.startsWith('/settings')) {
    return null
  }

  const found = findInRegistry(pathname)
  if (found) return found

  const prefix = resolvePrefix(pathname)
  const pageId = toPageId(pathname)

  return {
    pageId,
    label: humanizePath(pathname),
    match: pathname,
    domain: prefix?.domain ?? 'system',
    context: prefix?.context ?? 'system',
    relatedItemIds: [],
    fields: [...COMMON_LIST_FIELDS],
  }
}
